// AgriHarvestLayout.js - Agricultural Products Theme
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  StoreLogo, CartBtn, TabContent, StoreFooter, InlineEditableImage,
  AWS_URL, FaHeart
} from './ThemeLayoutHelpers';

// Custom InlineEditableTextWithNav component
const InlineEditableTextWithNav = ({ p, field, value, className = "", style = {}, children, onNavigate, navPage, isInModal = false }) => {
  const isEditing = p.previewEditMode && p.activeEditField === field;
  const isEditable = typeof p.onActivateField === "function";
  
  const getCurrentValue = () => {
    if (p.storeTheme && p.storeTheme[field] !== undefined && p.storeTheme[field] !== "") {
      return p.storeTheme[field];
    }
    // Also check p.themeText if available (for preview mode)
    if (p.themeText && p.themeText[field] !== undefined && p.themeText[field] !== "") {
      return p.themeText[field];
    }
    return value;
  };
  
  const [localValue, setLocalValue] = useState(getCurrentValue());

  useEffect(() => {
    if (!isEditing) {
      setLocalValue(getCurrentValue());
    }
  }, [p.storeTheme, field, value, isEditing, p.themeText]);

  const handleActivate = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const currentVal = getCurrentValue();
    setLocalValue(currentVal);
    if (typeof p.onActivateField === "function") {
      p.onActivateField(field);
    }
  };

  const handleChange = (event) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
  };

  const handleSaveAndClose = () => {
    if (typeof p.onPreviewTextChange === "function") {
      p.onPreviewTextChange(field, localValue);
    }
    if (typeof p.onDeactivateField === "function") {
      p.onDeactivateField();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleSaveAndClose();
      if (!isInModal && onNavigate && navPage) {
        onNavigate(navPage);
      }
    }
  };

  const handleBlur = () => {
    handleSaveAndClose();
  };

  if (isEditing) {
    const inputClass = "w-full rounded-2xl border border-green-200 bg-white/90 px-3 py-2 text-sm text-gray-900 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 dark:border-green-700 dark:bg-gray-800 dark:text-gray-100";
    const multiline = field.toLowerCase().includes("subtitle") || field.toLowerCase().includes("description") || field.toLowerCase().includes("overview") || field.toLowerCase().includes("content");

    return multiline ? (
      <textarea
        autoFocus
        rows={4}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    ) : (
      <input
        autoFocus
        type="text"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    );
  }

  const displayValue = p.storeTheme?.[field] || p.themeText?.[field] || value;
  
  return (
    <span
      className={`${className} ${isEditable ? 'cursor-pointer inline-block' : 'inline-block'}`}
      style={{ whiteSpace: "pre-wrap", ...style }}
      onClick={isEditable ? handleActivate : undefined}
      title={p.previewEditMode ? "Edit text" : undefined}
    >
      {displayValue}
    </span>
  );
};

export default function AgriHarvestLayout({ p }) {
  const T = p.storeTheme || {};
  const primary = T.primaryColor || "#166534";
  const secondary = T.secondaryColor || "#16a34a";
  
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [visibleProducts, setVisibleProducts] = useState([]);
  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [animationComplete, setAnimationComplete] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");
  
  const heroRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [p.storeTheme]);

  // Agricultural hero images - DIRECTLY FROM CONFIG
  const agriHeroImageConfigs = [
    {
      field: "themePreviewImage",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/9874ef83-7230-4e36-b810-1a08510a0f55_agri1.jpg",
    },
    {
      field: "agriHeroImage2",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/1a2a66be-1ba0-41af-8d3a-b55f1d9fd264_agri2.jpg",
    },
    {
      field: "agriHeroImage3",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/b85b1cc9-8eb3-4e8d-bd70-bbae3e247fdd_agri3.jpg",
    },
    {
      field: "agriHeroImage4",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/ee2d19e1-5388-41ea-8456-c1a37ad4212d_agri4.jpg",
    },
    {
      field: "agriHeroImage5",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/1ed79ef4-05ef-4348-a6e9-bd5273813dde_agri5.jpg",
    },
  ];

  // Agricultural Products - Added imageField
  const agriProducts = [
    // Seeds
    {
      id: 1,
      nameKey: "product1_name",
      name: "Premium Wheat Seeds (20kg)",
      priceKey: "product1_price",
      price: 899,
      originalPriceKey: "product1_original_price",
      originalPrice: 1199,
      imageField: "product1_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/a6257452-9d9c-4ee1-aa21-9222176ae668_Premium_Wheat_Seeds_(20kg).jpg",
      categoryKey: "product1_category",
      category: "Seeds",
      subCategory: "Cereal",
      descriptionKey: "product1_description",
      description: "High-yield, disease-resistant wheat variety suitable for all seasons.",
      badge: "Bestseller",
      badgeColor: "#166534",
      quantity: "20kg",
      brand: "AgriGold",
      yield: "40-45 q/acre",
      season: "Rabi & Kharif"
    },
    {
      id: 2,
      nameKey: "product2_name",
      name: "Hybrid Paddy Seeds (10kg)",
      priceKey: "product2_price",
      price: 649,
      originalPriceKey: "product2_original_price",
      originalPrice: 899,
      imageField: "product2_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/c74b42fb-a803-4316-ad06-026d04fcf8b9_Hybrid_Paddy_Seeds_(10kg).jpg",
      categoryKey: "product2_category",
      category: "Seeds",
      subCategory: "Paddy",
      descriptionKey: "product2_description",
      description: "High-yield hybrid paddy seeds with excellent grain quality.",
      badge: "Premium",
      badgeColor: "#f59e0b",
      quantity: "10kg",
      brand: "SeedMaster",
      yield: "50-55 q/acre",
      season: "Kharif"
    },
    {
      id: 3,
      nameKey: "product3_name",
      name: "Vegetable Seed Kit",
      priceKey: "product3_price",
      price: 399,
      originalPriceKey: "product3_original_price",
      originalPrice: 599,
      imageField: "product3_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/ed65b560-071a-4bf1-a871-30edb4852289_Vegetable_Seed_Kit.jpg",
      categoryKey: "product3_category",
      category: "Seeds",
      subCategory: "Vegetables",
      descriptionKey: "product3_description",
      description: "Complete vegetable seed kit with 10 varieties for home gardens.",
      badge: "Popular",
      badgeColor: "#16a34a",
      quantity: "20 packets",
      brand: "HomeGrow",
      yield: "Various",
      season: "All Seasons"
    },
    
    // Fertilizers
    {
      id: 4,
      nameKey: "product4_name",
      name: "NPK Complex Fertilizer (50kg)",
      priceKey: "product4_price",
      price: 1499,
      originalPriceKey: "product4_original_price",
      originalPrice: 1999,
      imageField: "product4_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/78dedc02-776b-4a34-83b0-dd44a531e68d_NPK_Complex_Fertilizer_(50kg).webp",
      categoryKey: "product4_category",
      category: "Fertilizers",
      subCategory: "Chemical",
      descriptionKey: "product4_description",
      description: "Balanced NPK fertilizer for all crop types and soil conditions.",
      badge: "Essential",
      badgeColor: "#166534",
      quantity: "50kg",
      brand: "NutriGrow",
      composition: "NPK 20-20-20",
      application: "Broadcast / Drip"
    },
    {
      id: 5,
      nameKey: "product5_name",
      name: "Organic Compost (25kg)",
      priceKey: "product5_price",
      price: 799,
      originalPriceKey: "product5_original_price",
      originalPrice: 1099,
      imageField: "product5_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/c1a26d13-f2d8-40f7-9c8c-39484275cb7d_Organic_Compost_(25kg).jpg",
      categoryKey: "product5_category",
      category: "Fertilizers",
      subCategory: "Organic",
      descriptionKey: "product5_description",
      description: "Rich organic compost for soil health and sustainable farming.",
      badge: "Organic",
      badgeColor: "#16a34a",
      quantity: "25kg",
      brand: "EcoGrow",
      composition: "100% Organic",
      application: "Soil Mixing"
    },
    {
      id: 6,
      nameKey: "product6_name",
      name: "Bio Fertilizer (5L)",
      priceKey: "product6_price",
      price: 449,
      originalPriceKey: "product6_original_price",
      originalPrice: 649,
      imageField: "product6_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/32d72c14-4005-4679-a345-60fbda0198b0_Bio_Fertilizer_(5L).webp",
      categoryKey: "product6_category",
      category: "Fertilizers",
      subCategory: "Bio",
      descriptionKey: "product6_description",
      description: "Liquid bio fertilizer for enhanced nutrient absorption.",
      badge: "Bio",
      badgeColor: "#8b5cf6",
      quantity: "5L",
      brand: "BioGrow",
      composition: "Microbial",
      application: "Foliar / Drip"
    },
    
    // Sprays & Pesticides
    {
      id: 7,
      nameKey: "product7_name",
      name: "Multi-Purpose Pesticide (1L)",
      priceKey: "product7_price",
      price: 599,
      originalPriceKey: "product7_original_price",
      originalPrice: 849,
      imageField: "product7_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/18ae0520-9c89-4e11-9902-18f2e31dd429_Multi-Purpose_Pesticide_(1L).jpg",
      categoryKey: "product7_category",
      category: "Sprays & Pesticides",
      subCategory: "Pesticides",
      descriptionKey: "product7_description",
      description: "Broad-spectrum pesticide for effective pest control in all crops.",
      badge: "Effective",
      badgeColor: "#dc2626",
      quantity: "1L",
      brand: "CropShield",
      type: "Contact & Systemic",
      coverage: "1-2 acres"
    },
    {
      id: 8,
      nameKey: "product8_name",
      name: "Herbicide (500ml)",
      priceKey: "product8_price",
      price: 399,
      originalPriceKey: "product8_original_price",
      originalPrice: 549,
      imageField: "product8_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/5f5adf9c-abd8-4292-a89b-488d24e83f42_Herbicide_(500ml).jpg",
      categoryKey: "product8_category",
      category: "Sprays & Pesticides",
      subCategory: "Herbicides",
      descriptionKey: "product8_description",
      description: "Selective herbicide for controlling broadleaf weeds in crops.",
      badge: "Weed Control",
      badgeColor: "#f59e0b",
      quantity: "500ml",
      brand: "WeedKill",
      type: "Selective",
      coverage: "1-3 acres"
    },
    {
      id: 9,
      nameKey: "product9_name",
      name: "Fungicide Spray (1L)",
      priceKey: "product9_price",
      price: 699,
      originalPriceKey: "product9_original_price",
      originalPrice: 999,
      imageField: "product9_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/336edff4-0feb-4be9-a236-7893b680d948_Fungicide_Spray_(1L).jpg",
      categoryKey: "product9_category",
      category: "Sprays & Pesticides",
      subCategory: "Fungicides",
      descriptionKey: "product9_description",
      description: "Advanced fungicide for prevention and treatment of fungal diseases.",
      badge: "Protection",
      badgeColor: "#8b5cf6",
      quantity: "1L",
      brand: "Funguard",
      type: "Systemic",
      coverage: "2-4 acres"
    },
    
    // Farming Tools
    {
      id: 10,
      nameKey: "product10_name",
      name: "Hand Sprayer (16L)",
      priceKey: "product10_price",
      price: 1299,
      originalPriceKey: "product10_original_price",
      originalPrice: 1799,
      imageField: "product10_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/66f32414-d942-47ea-9235-7d433bae4fc7_Hand_Sprayer_(16L).jpg",
      categoryKey: "product10_category",
      category: "Tools & Equipment",
      subCategory: "Sprayers",
      descriptionKey: "product10_description",
      description: "Durable hand sprayer for pesticides and fertilizers.",
      badge: "Durable",
      badgeColor: "#166534",
      quantity: "16L",
      brand: "FarmTech",
      type: "Manual",
      warranty: "1 Year"
    },
    {
      id: 11,
      nameKey: "product11_name",
      name: "Pruning Shears",
      priceKey: "product11_price",
      price: 299,
      originalPriceKey: "product11_original_price",
      originalPrice: 449,
      imageField: "product11_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/9fa2930f-78e9-43e6-9acb-5892a6c35d51_Pruning_Shears.jpg",
      categoryKey: "product11_category",
      category: "Tools & Equipment",
      subCategory: "Hand Tools",
      descriptionKey: "product11_description",
      description: "Professional pruning shears for precise cutting.",
      badge: "Precision",
      badgeColor: "#f59e0b",
      quantity: "Set",
      brand: "SharpEdge",
      type: "Hand Tool",
      warranty: "6 Months"
    },
    {
      id: 12,
      nameKey: "product12_name",
      name: "Soil Tester Kit",
      priceKey: "product12_price",
      price: 499,
      originalPriceKey: "product12_original_price",
      originalPrice: 699,
      imageField: "product12_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/523fb140-1aa1-46c4-af48-d911a016879a_pr2000049553.avif",
      categoryKey: "product12_category",
      category: "Tools & Equipment",
      subCategory: "Testing",
      descriptionKey: "product12_description",
      description: "Complete soil testing kit for pH, N, P, K analysis.",
      badge: "Smart Farming",
      badgeColor: "#16a34a",
      quantity: "Kit",
      brand: "SoilTest",
      type: "Test Kit",
      warranty: "6 Months"
    }
  ];

  // Categories
  const productCategories = [
    { key: "all", icon: "🌾", label: "All Products", color: "#166534" },
    { key: "seeds", icon: "🌱", label: "Seeds", color: "#16a34a" },
    { key: "fertilizers", icon: "🧪", label: "Fertilizers", color: "#f59e0b" },
    { key: "sprays", icon: "💧", label: "Sprays & Pesticides", color: "#dc2626" },
    { key: "tools", icon: "🔧", label: "Tools & Equipment", color: "#8b5cf6" },
  ];

  // Farming Guides - Added imageField
  const farmingGuides = [
    {
      id: 1,
      titleKey: "guide1_title",
      title: "Seed Selection Guide",
      descKey: "guide1_desc",
      desc: "How to choose the right seeds for your farm.",
      imageField: "guide1_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/447368f8-c585-4bad-889c-057eeab8d2a5_selection.jpg",
      steps: [
        "Understand your soil type and climate",
        "Choose disease-resistant varieties",
        "Check seed germination rates",
        "Consider market demand",
        "Select certified seeds from trusted brands"
      ],
      difficulty: "Beginner"
    },
    {
      id: 2,
      titleKey: "guide2_title",
      title: "Fertilizer Application",
      descKey: "guide2_desc",
      desc: "Proper fertilizer application for maximum yield.",
      imageField: "guide2_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/511f5707-c86f-4684-9237-e5442d3f0fe6_Fertilizer_Application.jpg",
      steps: [
        "Test soil for nutrient deficiency",
        "Choose NPK ratio based on crop",
        "Apply at the right growth stage",
        "Use proper application method",
        "Monitor crop response"
      ],
      difficulty: "Intermediate"
    },
    {
      id: 3,
      titleKey: "guide3_title",
      title: "Pest Management",
      descKey: "guide3_desc",
      desc: "Integrated pest management for healthy crops.",
      imageField: "guide3_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/8fddc4fc-fb08-4756-81b2-981a5abd429c_pest.jpg",
      steps: [
        "Regular field monitoring",
        "Identify pests and beneficial insects",
        "Use biological control methods",
        "Apply pesticides when needed",
        "Rotate crop varieties"
      ],
      difficulty: "Advanced"
    }
  ];

  // Seasonal Tips
  const seasonalTips = [
    { id: 1, icon: "🌱", title: "Kharif Season", desc: "June-October: Rice, Cotton, Sugarcane" },
    { id: 2, icon: "🌾", title: "Rabi Season", desc: "October-March: Wheat, Mustard, Peas" },
    { id: 3, icon: "☀️", title: "Zaid Season", desc: "March-June: Watermelon, Cucumber" },
    { id: 4, icon: "💧", title: "Irrigation Tips", desc: "Drip irrigation saves up to 50% water" },
  ];

  // Blog Posts - Added imageField
  const editorialPosts = [
    {
      id: 1,
      titleKey: "post1_title",
      title: "Modern Farming Techniques",
      descKey: "post1_desc",
      desc: "Innovative methods for sustainable agriculture.",
      fullContentKey: "post1_content",
      fullContent: "Modern agriculture is evolving rapidly with precision farming, AI-driven crop monitoring, and sustainable practices. Discover how technology is transforming traditional farming and helping farmers achieve higher yields with fewer resources.",
      imageField: "post1_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/2af4486d-4d06-4f26-a2a3-c5e3aa097525_Modern_Farming_Techniques.jpg",
      dateKey: "post1_date",
      date: "March 15, 2024",
      authorKey: "post1_author",
      author: "Dr. Arjun Singh",
      category: "Modern Farming"
    },
    {
      id: 2,
      titleKey: "post2_title",
      title: "Organic Farming Benefits",
      descKey: "post2_desc",
      desc: "Why organic farming is the future.",
      fullContentKey: "post2_content",
      fullContent: "Organic farming isn't just a trend—it's a sustainable approach to agriculture that benefits soil health, biodiversity, and human nutrition. Learn about the principles of organic farming and how it can lead to better yields and healthier food.",
      imageField: "post2_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/f9444ee5-21d4-403c-a179-f9574dc5801c_Organic_Farming_Benefits.jpg",
      dateKey: "post2_date",
      date: "March 10, 2024",
      authorKey: "post2_author",
      author: "Priya Mehta",
      category: "Organic Farming"
    },
    {
      id: 3,
      titleKey: "post3_title",
      title: "Crop Rotation Strategy",
      descKey: "post3_desc",
      desc: "Maximize yields with smart crop rotation.",
      fullContentKey: "post3_content",
      fullContent: "Crop rotation is one of the oldest and most effective farming practices. By alternating crops, farmers can improve soil fertility, reduce pest pressure, and increase overall productivity. Learn the best rotation strategies for your region.",
      imageField: "post3_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/6f9c51f5-2caa-40e1-b286-5a6fc5e0f218_Crop_Rotation_Strategy.jpg",
      dateKey: "post3_date",
      date: "March 5, 2024",
      authorKey: "post3_author",
      author: "Rajesh Kumar",
      category: "Farming Strategy"
    }
  ];

  // Navigation items
  const navItems = [
    { key: "nav_home", label: "HOME", page: "home" },
    { key: "nav_seeds", label: "SEEDS", page: "seeds" },
    { key: "nav_fertilizers", label: "FERTILIZERS", page: "fertilizers" },
    { key: "nav_sprays", label: "SPRAYS", page: "sprays" },
    { key: "nav_tools", label: "TOOLS", page: "tools" },
    { key: "nav_guides", label: "GUIDES", page: "guides" },
    { key: "nav_journal", label: "JOURNAL", page: "journal" },
    { key: "nav_about", label: "ABOUT", page: "about" },
  ];

  // Top bar messages
  const topBarMessages = [
    "🌾 QUALITY AGRICULTURAL PRODUCTS",
    "✦ FARMER'S TRUSTED PARTNER",
    "🌱 CERTIFIED SEEDS & FERTILIZERS",
    "✦ FREE DELIVERY ON BULK ORDERS"
  ];

  // Floating particles
  const particles = Array.from({ length: 25 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    size: 3 + Math.random() * 6,
    delay: Math.random() * 5,
    duration: 5 + Math.random() * 5,
    opacity: 0.05 + Math.random() * 0.15,
  }));

  // Auto-rotate hero images - USING CONFIG DIRECTLY
  useEffect(() => {
    if (p.previewEditMode) return;
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % agriHeroImageConfigs.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [p.previewEditMode]);

  // Animate products
  useEffect(() => {
    const products = p.filteredProducts || [];
    if (products.length === 0) return;
    
    setTimeout(() => setAnimationComplete(true), 300);
    
    products.forEach((_, index) => {
      setTimeout(() => {
        setVisibleProducts(prev => [...prev, index]);
      }, index * 80 + 500);
    });
  }, [p.filteredProducts]);

  // Mouse move for parallax
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 15,
        y: (e.clientY / window.innerHeight - 0.5) * 10,
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Scroll handler
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigateTo = useCallback((page, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [p.previewEditMode, p.activeEditField]);

  const handleNavigateAfterEdit = useCallback((page) => {
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const openBlogModal = useCallback((post, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedPost(post);
  }, [p.previewEditMode, p.activeEditField]);

  const closeBlogModal = useCallback(() => {
    setSelectedPost(null);
  }, []);

  const openGuideModal = useCallback((guide, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedGuide(guide);
  }, [p.previewEditMode, p.activeEditField]);

  const closeGuideModal = useCallback(() => {
    setSelectedGuide(null);
  }, []);

  const handleBackdropClick = useCallback((event) => {
    if (event.target === event.currentTarget) {
      if (p.previewEditMode && p.activeEditField) {
        return;
      }
      setSelectedProduct(null);
      setSelectedPost(null);
      setSelectedGuide(null);
    }
  }, [p.previewEditMode, p.activeEditField]);

  const handleEditHeroImage = useCallback((event, field = null) => {
    event.preventDefault();
    event.stopPropagation();
    if (!p.previewEditMode) {
      return;
    }
    const targetField = field || agriHeroImageConfigs[activeSlide]?.field || "themePreviewImage";
    const targetIndex = agriHeroImageConfigs.findIndex((image) => image.field === targetField);
    if (targetIndex >= 0) {
      setActiveSlide(targetIndex);
    }
    if (typeof p.onImageFieldClick === "function") {
      p.onImageFieldClick(targetField, event);
      return;
    }
    if (typeof p.onActivateField === "function") {
      p.onActivateField(targetField);
    }
  }, [activeSlide, agriHeroImageConfigs, p]);

  const copy = {
    eyebrow: p.storeTheme?.heroEyebrow || "AGRI HARVEST",
    title: p.storeTheme?.heroTitle || "Nourishing the Future",
    subtitle: p.storeTheme?.heroSubtitle || "Premium agricultural products for thriving farms",
    primaryCta: p.storeTheme?.primaryCtaText || "Explore Products",
    secondaryCta: p.storeTheme?.secondaryCtaText || "View Guides",
  };

  // Get editable products with imageField
  const editableAgriProducts = agriProducts.map((product) => ({
    ...product,
    image: p.storeTheme?.[product.imageField] || product.image,
  }));

  // Get editable guides with imageField
  const editableFarmingGuides = farmingGuides.map((guide) => ({
    ...guide,
    image: p.storeTheme?.[guide.imageField] || guide.image,
  }));

  // Get editable posts with imageField
  const editableEditorialPosts = editorialPosts.map((post) => ({
    ...post,
    image: p.storeTheme?.[post.imageField] || post.image,
  }));

  // Get filtered items based on category
  const getFilteredItems = (category) => {
    if (category === "all") return editableAgriProducts;
    const map = {
      "seeds": "Seeds",
      "fertilizers": "Fertilizers",
      "sprays": "Sprays & Pesticides",
      "tools": "Tools & Equipment",
    };
    return editableAgriProducts.filter(item => item.category === map[category]);
  };

  const filteredItems = getFilteredItems(activeCategory);

  // Get card entrance animation
  const getCardEntrance = (index) => {
    const directions = [
      { x: -40, y: -15, rotate: -3 },
      { x: 40, y: -15, rotate: 3 },
      { x: -40, y: 15, rotate: -2 },
      { x: 40, y: 15, rotate: 2 },
    ];
    return directions[index % directions.length];
  };

  return (
    <div key={refreshKey} className="w-full min-h-screen bg-green-50 overflow-x-hidden" style={{ fontFamily: T.fontFamily || "'DM Sans', sans-serif" }}>
      
      {/* Floating Particles */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute rounded-full animate-float-particle"
            style={{
              left: `${particle.left}%`,
              top: `${particle.top}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              background: `radial-gradient(circle, ${primary}${Math.floor(20 + Math.random() * 40).toString(16)}, transparent)`,
              animationDelay: `${particle.delay}s`,
              animationDuration: `${particle.duration}s`,
              opacity: particle.opacity,
            }}
          />
        ))}
      </div>
      
      {/* Top Bar */}
      <div className="relative bg-green-900 text-green-200 py-2 overflow-hidden">
        <div className="whitespace-nowrap animate-marquee">
          {topBarMessages.map((msg, idx) => (
            <span key={idx} className="inline-block mx-8 text-xs tracking-wider">
              <InlineEditableTextWithNav 
                p={p} 
                field={`top_bar_message_${idx + 1}`} 
                value={msg}
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </span>
          ))}
        </div>
      </div>
      
      {/* Navigation */}
      <nav className={`sticky top-0 z-50 transition-all duration-500 ${isScrolled ? "bg-green-900/95 backdrop-blur-xl shadow-lg py-2" : "bg-green-900/90 backdrop-blur-sm py-4"}`}>
        <div className="w-full px-4 md:px-8 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={(e) => navigateTo("home", e)}>
            <StoreLogo storeInfo={p.storeInfo} border={`border-2 ${isScrolled ? "border-green-400" : "border-green-300"}`} />
            <span className="font-bold text-xl tracking-tight text-white">
              <InlineEditableTextWithNav 
                p={p} 
                field="businessName" 
                value={p.storeInfo?.businessName || "Agri Harvest"} 
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </span>
          </div>
          
          <button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!(p.previewEditMode && p.activeEditField)) {
                setMobileMenuOpen(!mobileMenuOpen);
              }
            }} 
            className="md:hidden p-2"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          <div className="hidden md:flex items-center gap-6">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => navigateTo(item.page, e)}
                className={`text-sm font-medium transition-all duration-300 relative group ${
                  activePage === item.page 
                    ? "text-green-300" 
                    : "text-white/80 hover:text-white"
                }`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={`nav_${item.page}`} 
                  value={item.label}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={item.page}
                  className="text-inherit"
                />
                <span className={`absolute -bottom-1 left-0 h-0.5 bg-green-400 transition-all duration-300 ${activePage === item.page ? "w-full" : "w-0 group-hover:w-full"}`} />
              </button>
            ))}
          </div>
          
          <div className="flex items-center gap-3">
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  p.handleGoToWishlist?.();
                }
              }} 
              className="relative group"
            >
              <div className="p-2 rounded-full transition-all duration-300 hover:bg-green-800/50">
                <FaHeart className="text-lg text-white/80 hover:text-white transition-colors" />
              </div>
              {p.wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-green-500 text-white text-[10px] flex items-center justify-center font-bold animate-pulse">
                  {p.wishlistCount}
                </span>
              )}
            </button>
            <CartBtn p={p} dark={true} />
            {p.userMenuElement}
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-green-900 shadow-lg py-4 px-4 flex flex-col gap-2 z-50 border-t border-green-800">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => navigateTo(item.page, e)}
                className={`text-sm py-2 px-3 rounded-lg text-left ${
                  activePage === item.page 
                    ? "bg-green-800/50 text-green-300" 
                    : "text-white/80 hover:text-white"
                }`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={`nav_${item.page}`} 
                  value={item.label}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={item.page}
                  className="text-inherit"
                />
              </button>
            ))}
          </div>
        )}
      </nav>
      
      {/* ==================== HOME PAGE ==================== */}
      {activePage === "home" && (
        <>
          {/* Hero Section - FIXED: DIRECTLY USING CONFIG IMAGES - REMOVED HERO BUTTONS */}
          <section className="relative w-full h-screen min-h-[700px] overflow-hidden -mt-[60px]">
            <div className="absolute inset-0 w-full h-full">
              {agriHeroImageConfigs.map((img, idx) => (
                <div
                  key={img.field}
                  className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out ${
                    idx === activeSlide ? "opacity-100 z-10" : "opacity-0 z-0"
                  }`}
                >
                  <InlineEditableImage p={p} field={img.field} className="h-full w-full">
                    <img 
                      src={p.storeTheme?.[img.field] || img.src} 
                      alt={`Agriculture ${idx + 1}`} 
                      className="w-full h-full object-cover object-center" 
                      style={{ 
                        transform: `translate(${mousePosition.x * 0.3}px, ${mousePosition.y * 0.3}px) scale(1.05)`, 
                        transition: "transform 0.3s ease-out" 
                      }} 
                    />
                  </InlineEditableImage>
                </div>
              ))}
              <div className="absolute inset-0 bg-gradient-to-r from-green-900/80 via-green-900/40 to-transparent z-20 pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-t from-green-50 via-transparent to-transparent z-20 pointer-events-none" />
            </div>
            
            <div className="relative z-30 h-full w-full px-4 md:px-8 flex items-center">
              <div className="max-w-3xl">
                <div className="inline-block mb-6 animate-bounce-in">
                  <div className="flex items-center gap-3 px-5 py-2.5 bg-green-800/30 backdrop-blur-md rounded-full border border-green-400/30">
                    <span className="w-2 h-2 rounded-full bg-green-400 animate-ping" />
                    <span className="text-green-200 text-xs tracking-[0.2em] uppercase">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="heroEyebrow" 
                        value={copy.eyebrow}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="home"
                      />
                    </span>
                    <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  </div>
                </div>
                <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-[1.05] tracking-tight">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroTitle" 
                    value={copy.title}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  >
                    {copy.title.split(" ").map((word, i) => (
                      <span key={i} className="inline-block animate-word-pop" style={{ animationDelay: `${i * 0.15}s` }}>{word}{" "}</span>
                    ))}
                  </InlineEditableTextWithNav>
                </h1>
                <p className="mt-6 text-green-100/90 text-lg max-w-xl animate-slide-right">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroSubtitle" 
                    value={copy.subtitle}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  />
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <button 
                    onClick={(e) => navigateTo("seeds", e)} 
                    className="group px-8 py-4 rounded-full bg-green-600 text-white font-semibold tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-green-600/30 flex items-center gap-2 animate-slide-up"
                  >
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="primaryCtaText" 
                      value={copy.primaryCta}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="seeds"
                    />
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </button>
                  <button 
                    onClick={(e) => navigateTo("guides", e)} 
                    className="px-8 py-4 rounded-full border-2 border-green-400/50 text-green-200 font-semibold tracking-wide transition-all duration-300 hover:bg-green-600/30 hover:border-green-300 backdrop-blur-sm animate-slide-up"
                  >
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="secondaryCtaText" 
                      value={copy.secondaryCta}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="guides"
                    />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce-slow z-30">
              <div className="flex flex-col items-center gap-1">
                <span className="text-green-200/60 text-[10px] tracking-wider">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="scroll_text" 
                    value="SCROLL TO EXPLORE"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  />
                </span>
                <div className="w-5 h-8 border-2 border-green-400/30 rounded-full flex justify-center">
                  <div className="w-1 h-2 bg-green-400/60 rounded-full mt-1 animate-scroll-dot" />
                </div>
              </div>
            </div>
          </section>
          
          {/* Featured Badge - Positioned lower */}
          <div className="relative z-10 -mt-0 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-full shadow-lg border border-green-200">
              <span className="text-green-600 text-sm">🌾</span>
              <span className="text-green-900 text-xs font-semibold tracking-wider">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="collection_badge" 
                  value="TRUSTED BY FARMERS"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </span>
              <span className="text-green-600 text-sm">🌾</span>
            </div>
          </div>
          
          {/* Seasonal Tips */}
          <section className="w-full px-4 md:px-8 py-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {seasonalTips.map((tip, idx) => (
                <div 
                  key={tip.id} 
                  className="group cursor-pointer animate-card-fly" 
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <div className="relative overflow-hidden rounded-2xl p-4 text-center transition-all duration-500 hover:-translate-y-2 hover:shadow-xl bg-white border border-green-200 hover:border-green-400">
                    <div className="text-4xl mb-2 transition-all duration-300 group-hover:scale-110">
                      {tip.icon}
                    </div>
                    <h3 className="font-semibold text-green-800 text-sm">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={`season_${tip.id}_title`} 
                        value={tip.title}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="home"
                      />
                    </h3>
                    <p className="text-xs text-gray-600 mt-1">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={`season_${tip.id}_desc`} 
                        value={tip.desc}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="home"
                      />
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* Category Grid */}
          <section className="w-full px-4 md:px-8 py-8">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {productCategories.map((cat, idx) => (
                <div 
                  key={cat.key} 
                  className="group cursor-pointer animate-card-fly" 
                  style={{ animationDelay: `${idx * 0.08}s` }}
                  onClick={(e) => {
                    if (cat.key === "all") {
                      navigateTo("seeds", e);
                    } else {
                      setActiveCategory(cat.key);
                      navigateTo(cat.key === "seeds" ? "seeds" : cat.key === "fertilizers" ? "fertilizers" : cat.key === "sprays" ? "sprays" : "tools", e);
                    }
                  }}
                >
                  <div className="relative overflow-hidden rounded-2xl p-4 text-center transition-all duration-500 hover:-translate-y-2 hover:shadow-xl bg-white border border-green-200 hover:border-green-400">
                    <div className="text-3xl mb-2 transition-all duration-300 group-hover:scale-110">
                      {cat.icon}
                    </div>
                    <h3 className="font-semibold text-green-800 text-xs">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={`cat_${cat.key}`} 
                        value={cat.label}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="seeds"
                      />
                    </h3>
                    <div className="mt-1 h-0.5 w-0 group-hover:w-full transition-all duration-500 mx-auto" style={{ backgroundColor: cat.color }} />
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* Featured Products - FIXED with InlineEditableImage - REMOVED ADD TO CART */}
          <section className="w-full px-4 md:px-8 py-8 bg-white">
            <div className="text-center mb-10">
              <span className="text-green-700 text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="featured_badge" 
                  value="Our Products"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-green-900 mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="featured_title" 
                  value="Featured Agricultural Products"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-green-600 to-green-400 mx-auto mt-4 rounded-full" />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {editableAgriProducts.slice(0, 4).map((product, idx) => {
                const entrance = getCardEntrance(idx);
                const isVisible = animationComplete;
                return (
                  <div 
                    key={product.id} 
                    className={`group cursor-pointer transition-all duration-700 ${isVisible ? "opacity-100 translate-x-0 translate-y-0 rotate-0" : `opacity-0 translate-x-[${entrance.x}px] translate-y-[${entrance.y}px] rotate-[${entrance.rotate}deg]`}`}
                    onMouseEnter={() => setHoveredCard(idx)}
                    onMouseLeave={() => setHoveredCard(null)}
                    onClick={() => setSelectedProduct(product)}
                  >
                    <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-green-100 hover:border-green-300">
                      <div className="relative h-48 overflow-hidden bg-green-50">
                        <InlineEditableImage p={p} field={product.imageField} className="block h-full w-full overflow-hidden">
                          <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                        </InlineEditableImage>
                        {product.badge && (
                          <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: product.badgeColor }}>
                            {product.badge}
                          </div>
                        )}
                        <div className="absolute top-2 right-2 bg-green-700/80 text-green-100 text-[10px] font-bold px-2 py-0.5 rounded-full">
                          {product.quantity}
                        </div>
                      </div>
                      <div className="p-3">
                        <div className="flex items-center gap-1 mb-1">
                          <span className="text-xs text-gray-500">{product.brand}</span>
                          <span className="text-xs text-gray-300">•</span>
                          <span className="text-xs text-gray-500">{product.category}</span>
                        </div>
                        <h3 className="font-semibold text-gray-800 text-sm line-clamp-1">
                          <InlineEditableTextWithNav 
                            p={p} 
                            field={product.nameKey} 
                            value={product.name}
                            onNavigate={handleNavigateAfterEdit}
                            navPage="home"
                          />
                        </h3>
                        <div className="mt-2 flex items-center justify-between">
                          <div>
                            <span className="text-lg font-bold text-green-700">₹<InlineEditableTextWithNav 
                              p={p} 
                              field={product.priceKey} 
                              value={product.price.toString()}
                              onNavigate={handleNavigateAfterEdit}
                              navPage="home"
                            /></span>
                            {product.originalPrice > product.price && (
                              <span className="text-xs text-gray-400 line-through ml-1">₹{product.originalPrice}</span>
                            )}
                          </div>
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                            {product.subCategory}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="text-center mt-10">
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!(p.previewEditMode && p.activeEditField)) {
                    setActiveCategory("all");
                    setActivePage("seeds");
                    setMobileMenuOpen(false);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                }} 
                className="px-8 py-3 rounded-full bg-green-700 text-white font-semibold hover:bg-green-800 transition-all duration-300 hover:scale-105"
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field="view_all_button" 
                  value="View All Products"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="seeds"
                />
              </button>
            </div>
          </section>
          
          {/* Farming Guides Preview - FIXED with InlineEditableImage */}
          <section className="w-full px-4 md:px-8 py-8">
            <div className="text-center mb-8">
              <span className="text-green-700 text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="guides_badge" 
                  value="Learn & Grow"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="guides"
                />
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {editableFarmingGuides.map((guide) => (
                <div 
                  key={guide.id} 
                  className="group cursor-pointer rounded-2xl overflow-hidden bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-green-200 hover:border-green-400"
                  onClick={() => openGuideModal(guide)}
                >
                  <div className="h-40 overflow-hidden">
                    <InlineEditableImage p={p} field={guide.imageField} className="block h-full w-full overflow-hidden">
                      <img src={guide.image} alt={guide.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    </InlineEditableImage>
                  </div>
                  <div className="p-4">
                    <h4 className="font-semibold text-green-800 text-sm">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={guide.titleKey} 
                        value={guide.title}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="guides"
                      />
                    </h4>
                    <p className="text-xs text-gray-600 mt-1">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={guide.descKey} 
                        value={guide.desc}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="guides"
                      />
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                        {guide.difficulty}
                      </span>
                      <span className="text-xs text-green-600 font-semibold group-hover:gap-2 transition-all flex items-center gap-1">
                        Read More <span>→</span>
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
      
      {/* ==================== SEEDS PAGE ==================== */}
      {activePage === "seeds" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-green-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="seeds_badge" 
                value="Quality Seeds"
                onNavigate={handleNavigateAfterEdit}
                navPage="seeds"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-green-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="seeds_title" 
                value="Premium Seeds"
                onNavigate={handleNavigateAfterEdit}
                navPage="seeds"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="seeds_subtitle" 
                value="High-quality seeds for maximum yield and quality"
                onNavigate={handleNavigateAfterEdit}
                navPage="seeds"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-green-600 to-green-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {editableAgriProducts.filter(d => d.category === "Seeds").map((product) => (
              <div 
                key={product.id} 
                className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-green-200 hover:border-green-400"
                onClick={() => setSelectedProduct(product)}
              >
                <div className="relative h-48 overflow-hidden bg-green-50">
                  <InlineEditableImage p={p} field={product.imageField} className="block h-full w-full overflow-hidden">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                  {product.badge && (
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: product.badgeColor }}>
                      {product.badge}
                    </div>
                  )}
                  <div className="absolute top-2 right-2 bg-green-700/80 text-green-100 text-[10px] font-bold px-2 py-0.5 rounded-full">
                    {product.quantity}
                  </div>
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-xs text-gray-500">{product.brand}</span>
                    <span className="text-xs text-gray-300">•</span>
                    <span className="text-xs text-gray-500">{product.subCategory}</span>
                  </div>
                  <h3 className="font-semibold text-gray-800 text-sm">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={product.nameKey} 
                      value={product.name}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="seeds"
                    />
                  </h3>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-lg font-bold text-green-700">₹<InlineEditableTextWithNav 
                      p={p} 
                      field={product.priceKey} 
                      value={product.price.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="seeds"
                    /></span>
                    {product.originalPrice > product.price && (
                      <span className="text-xs text-gray-400 line-through">₹{product.originalPrice}</span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    Yield: {product.yield} • Season: {product.season}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== FERTILIZERS PAGE ==================== */}
      {activePage === "fertilizers" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-yellow-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="fertilizers_badge" 
                value="Fertilizers"
                onNavigate={handleNavigateAfterEdit}
                navPage="fertilizers"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-yellow-800 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="fertilizers_title" 
                value="Quality Fertilizers"
                onNavigate={handleNavigateAfterEdit}
                navPage="fertilizers"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="fertilizers_subtitle" 
                value="Essential nutrients for healthy crop growth"
                onNavigate={handleNavigateAfterEdit}
                navPage="fertilizers"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-yellow-600 to-yellow-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {editableAgriProducts.filter(d => d.category === "Fertilizers").map((product) => (
              <div 
                key={product.id} 
                className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-yellow-200 hover:border-yellow-400"
                onClick={() => setSelectedProduct(product)}
              >
                <div className="relative h-48 overflow-hidden bg-yellow-50">
                  <InlineEditableImage p={p} field={product.imageField} className="block h-full w-full overflow-hidden">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                  {product.badge && (
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: product.badgeColor }}>
                      {product.badge}
                    </div>
                  )}
                  <div className="absolute top-2 right-2 bg-yellow-700/80 text-yellow-100 text-[10px] font-bold px-2 py-0.5 rounded-full">
                    {product.quantity}
                  </div>
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-xs text-gray-500">{product.brand}</span>
                    <span className="text-xs text-gray-300">•</span>
                    <span className="text-xs text-gray-500">{product.subCategory}</span>
                  </div>
                  <h3 className="font-semibold text-gray-800 text-sm">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={product.nameKey} 
                      value={product.name}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="fertilizers"
                    />
                  </h3>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-lg font-bold text-yellow-700">₹<InlineEditableTextWithNav 
                      p={p} 
                      field={product.priceKey} 
                      value={product.price.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="fertilizers"
                    /></span>
                    {product.originalPrice > product.price && (
                      <span className="text-xs text-gray-400 line-through">₹{product.originalPrice}</span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    Composition: {product.composition} • {product.application}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== SPRAYS PAGE ==================== */}
      {activePage === "sprays" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-red-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="sprays_badge" 
                value="Sprays & Pesticides"
                onNavigate={handleNavigateAfterEdit}
                navPage="sprays"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-red-800 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="sprays_title" 
                value="Crop Protection"
                onNavigate={handleNavigateAfterEdit}
                navPage="sprays"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="sprays_subtitle" 
                value="Effective solutions for pest and disease management"
                onNavigate={handleNavigateAfterEdit}
                navPage="sprays"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-red-600 to-red-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {editableAgriProducts.filter(d => d.category === "Sprays & Pesticides").map((product) => (
              <div 
                key={product.id} 
                className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-red-200 hover:border-red-400"
                onClick={() => setSelectedProduct(product)}
              >
                <div className="relative h-48 overflow-hidden bg-red-50">
                  <InlineEditableImage p={p} field={product.imageField} className="block h-full w-full overflow-hidden">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                  {product.badge && (
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: product.badgeColor }}>
                      {product.badge}
                    </div>
                  )}
                  <div className="absolute top-2 right-2 bg-red-700/80 text-red-100 text-[10px] font-bold px-2 py-0.5 rounded-full">
                    {product.quantity}
                  </div>
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-xs text-gray-500">{product.brand}</span>
                    <span className="text-xs text-gray-300">•</span>
                    <span className="text-xs text-gray-500">{product.subCategory}</span>
                  </div>
                  <h3 className="font-semibold text-gray-800 text-sm">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={product.nameKey} 
                      value={product.name}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="sprays"
                    />
                  </h3>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-lg font-bold text-red-700">₹<InlineEditableTextWithNav 
                      p={p} 
                      field={product.priceKey} 
                      value={product.price.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="sprays"
                    /></span>
                    {product.originalPrice > product.price && (
                      <span className="text-xs text-gray-400 line-through">₹{product.originalPrice}</span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    Type: {product.type} • Coverage: {product.coverage}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== TOOLS PAGE ==================== */}
      {activePage === "tools" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-purple-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="tools_badge" 
                value="Tools & Equipment"
                onNavigate={handleNavigateAfterEdit}
                navPage="tools"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-purple-800 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="tools_title" 
                value="Farm Equipment"
                onNavigate={handleNavigateAfterEdit}
                navPage="tools"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="tools_subtitle" 
                value="Reliable tools for efficient farming"
                onNavigate={handleNavigateAfterEdit}
                navPage="tools"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-purple-600 to-purple-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {editableAgriProducts.filter(d => d.category === "Tools & Equipment").map((product) => (
              <div 
                key={product.id} 
                className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-purple-200 hover:border-purple-400"
                onClick={() => setSelectedProduct(product)}
              >
                <div className="relative h-48 overflow-hidden bg-purple-50">
                  <InlineEditableImage p={p} field={product.imageField} className="block h-full w-full overflow-hidden">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                  {product.badge && (
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: product.badgeColor }}>
                      {product.badge}
                    </div>
                  )}
                  <div className="absolute top-2 right-2 bg-purple-700/80 text-purple-100 text-[10px] font-bold px-2 py-0.5 rounded-full">
                    {product.quantity}
                  </div>
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-xs text-gray-500">{product.brand}</span>
                    <span className="text-xs text-gray-300">•</span>
                    <span className="text-xs text-gray-500">{product.subCategory}</span>
                  </div>
                  <h3 className="font-semibold text-gray-800 text-sm">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={product.nameKey} 
                      value={product.name}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="tools"
                    />
                  </h3>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-lg font-bold text-purple-700">₹<InlineEditableTextWithNav 
                      p={p} 
                      field={product.priceKey} 
                      value={product.price.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="tools"
                    /></span>
                    {product.originalPrice > product.price && (
                      <span className="text-xs text-gray-400 line-through">₹{product.originalPrice}</span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    Type: {product.type} • Warranty: {product.warranty}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== GUIDES PAGE ==================== */}
      {activePage === "guides" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-green-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="guides_page_badge" 
                value="Guides"
                onNavigate={handleNavigateAfterEdit}
                navPage="guides"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-green-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="guides_page_title" 
                value="Farming Guides"
                onNavigate={handleNavigateAfterEdit}
                navPage="guides"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="guides_page_subtitle" 
                value="Learn from the experts with our comprehensive farming guides"
                onNavigate={handleNavigateAfterEdit}
                navPage="guides"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-green-600 to-green-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {editableFarmingGuides.map((guide) => (
              <div 
                key={guide.id} 
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer border border-green-200 hover:border-green-400"
                onClick={() => openGuideModal(guide)}
              >
                <div className="h-56 overflow-hidden">
                  <InlineEditableImage p={p} field={guide.imageField} className="block h-full w-full overflow-hidden">
                    <img src={guide.image} alt={guide.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full font-semibold">
                      {guide.difficulty}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-green-900 mb-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={guide.titleKey} 
                      value={guide.title}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="guides"
                    />
                  </h3>
                  <p className="text-gray-600">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={guide.descKey} 
                      value={guide.desc}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="guides"
                    />
                  </p>
                  <div className="mt-4 text-green-700 font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="read_guide_button" 
                      value="Read Guide"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="guides"
                    />
                    <span>→</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== JOURNAL PAGE ==================== */}
      {activePage === "journal" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-green-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_badge" 
                value="Journal"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-green-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_title" 
                value="Agriculture Insights"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_subtitle" 
                value="Latest trends and insights in agriculture"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-green-600 to-green-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {editableEditorialPosts.map((post) => (
              <div 
                key={post.id} 
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer border border-green-200 hover:border-green-400"
                onClick={(e) => openBlogModal(post, e)}
              >
                <div className="h-56 overflow-hidden">
                  <InlineEditableImage p={p} field={post.imageField} className="block h-full w-full overflow-hidden">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-green-600 font-semibold">{post.category}</span>
                    <span className="text-xs text-gray-400">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={post.dateKey} 
                        value={post.date}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-green-900 mb-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={post.titleKey} 
                      value={post.title}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                    />
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={post.descKey} 
                      value={post.desc}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                    />
                  </p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-gray-400">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="by_prefix" 
                        value="By"
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />{' '}
                      <span className="font-semibold text-green-800">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={post.authorKey} 
                          value={post.author}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="journal"
                        />
                      </span>
                    </span>
                    <button 
                      onClick={(e) => openBlogModal(post, e)}
                      className="text-green-700 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all"
                    >
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="read_more_button" 
                        value="Read More"
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                      <span>→</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== ABOUT PAGE ==================== */}
      {activePage === "about" && (
        <section className="w-full px-4 md:px-8 py-12 md:py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-green-700 text-sm tracking-wider uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_badge" 
                  value="About Us"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-green-900 mt-3">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_title" 
                  value="Empowering Indian Agriculture"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </h2>
              <div className="w-16 h-1 bg-green-600 mt-4 mb-6" />
              <p className="text-gray-600 leading-relaxed mb-4">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para1" 
                  value="Agri Harvest is dedicated to providing Indian farmers with premium quality agricultural products. From high-yield seeds to effective crop protection solutions, we are committed to helping farmers achieve better yields and sustainable farming practices."
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para2" 
                  value="Our team of agricultural experts carefully selects every product to ensure quality, effectiveness, and value for money. We work directly with leading manufacturers to bring you the best agricultural inputs at competitive prices."
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </p>
              <div className="flex flex-wrap gap-6">
                <div>
                  <p className="text-3xl font-bold text-green-700">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat1_value" 
                      value="100+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat1_label" 
                      value="Products"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-green-700">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat2_value" 
                      value="50+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat2_label" 
                      value="Trusted Brands"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-green-700">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat3_value" 
                      value="10k+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat3_label" 
                      value="Satisfied Farmers"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <InlineEditableImage p={p} field="agriAboutImage" className="block h-full w-full overflow-hidden">
                <img 
                  src={p.storeTheme?.agriAboutImage || "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/511f5707-c86f-4684-9237-e5442d3f0fe6_Fertilizer_Application.jpg"} 
                  alt="About Agri Harvest" 
                  className="w-full h-full object-cover" 
                />
              </InlineEditableImage>
            </div>
          </div>
        </section>
      )}
      
      {/* ==================== PRODUCT TABS ==================== */}
      <section className="w-full px-4 md:px-8 mt-8">
        <div className="flex justify-center gap-2 flex-wrap">
          {[
            ["products", "🌾 Shop Agriculture"],
            ["services", "🛠 Services"],
            ["details", "📖 About Store"]
          ].map(([key, label]) => (
            <button 
              key={key} 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  p.setActiveTab(key);
                }
              }} 
              className={`px-5 md:px-7 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${p.activeTab === key ? "bg-green-700 text-white shadow-lg" : "text-gray-500 hover:text-green-700"}`}
            >
              <InlineEditableTextWithNav 
                p={p} 
                field={`tab_${key}_label`} 
                value={label}
                onNavigate={handleNavigateAfterEdit}
                navPage={activePage}
              />
            </button>
          ))}
        </div>
      </section>
      
      {/* Main Content */}
      <section className="flex-1 w-full px-4 md:px-8 py-8 md:py-12">
        <TabContent p={p} />
      </section>
      
      {/* Newsletter */}
      <section className="bg-gradient-to-r from-green-800 to-green-900 py-16 mt-8 w-full">
        <div className="max-w-3xl mx-auto text-center px-4">
          <span className="text-green-300 text-sm tracking-wider uppercase">
            <InlineEditableTextWithNav 
              p={p} 
              field="newsletter_badge" 
              value="Subscribe"
              onNavigate={handleNavigateAfterEdit}
              navPage="home"
            />
          </span>
          <h3 className="text-2xl md:text-3xl font-bold text-white mt-2">
            <InlineEditableTextWithNav 
              p={p} 
              field="newsletter_title" 
              value="Join the Agri Community"
              onNavigate={handleNavigateAfterEdit}
              navPage="home"
            />
          </h3>
          <p className="text-green-200 text-sm mt-2">
            <InlineEditableTextWithNav 
              p={p} 
              field="newsletter_subtitle" 
              value="Get farming tips, product updates, and exclusive offers"
              onNavigate={handleNavigateAfterEdit}
              navPage="home"
            />
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input 
              type="email" 
              placeholder={p.storefrontText?.email_placeholder || "Your email address"} 
              className="flex-1 px-5 py-3 bg-white/10 border border-green-400/30 rounded-full text-white placeholder-green-300/50 focus:border-green-400 outline-none transition" 
            />
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  // Handle newsletter subscription
                }
              }}
              className="px-6 py-3 rounded-full bg-green-600 text-white font-semibold hover:bg-green-500 transition"
            >
              <InlineEditableTextWithNav 
                p={p} 
                field="subscribe_button" 
                value="Subscribe"
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </button>
          </div>
        </div>
      </section>
      
      <StoreFooter p={p} />
      
      {/* ==================== MODALS ==================== */}
      
      {/* Product Modal - FIXED with InlineEditableImage */}
      {selectedProduct && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-md w-full bg-white rounded-2xl overflow-hidden shadow-2xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-64">
              <InlineEditableImage p={p} field={selectedProduct.imageField} className="block h-full w-full overflow-hidden">
                <img src={selectedProduct.image} alt={selectedProduct.name} className="w-full h-full object-cover" />
              </InlineEditableImage>
              <button 
                onClick={() => setSelectedProduct(null)} 
                className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/50 text-white hover:bg-green-600 transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
              {selectedProduct.badge && (
                <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-white text-xs font-bold shadow-lg" style={{ backgroundColor: selectedProduct.badgeColor }}>
                  {selectedProduct.badge}
                </div>
              )}
              <div className="absolute bottom-3 right-3 bg-green-700/80 text-green-100 text-xs font-bold px-2 py-1 rounded-full">
                {selectedProduct.quantity}
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-gray-500">{selectedProduct.brand}</span>
                <span className="text-xs text-gray-300">•</span>
                <span className="text-xs text-gray-500">{selectedProduct.category}</span>
                <span className="text-xs text-gray-300">•</span>
                <span className="text-xs text-gray-500">{selectedProduct.subCategory}</span>
              </div>
              <h3 className="text-2xl font-bold text-green-900">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedProduct.nameKey} 
                  value={selectedProduct.name}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </h3>
              <p className="text-gray-600 text-sm mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedProduct.descriptionKey} 
                  value={selectedProduct.description}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                  {selectedProduct.subCategory}
                </span>
                {selectedProduct.yield && (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                    Yield: {selectedProduct.yield}
                  </span>
                )}
                {selectedProduct.season && (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                    {selectedProduct.season}
                  </span>
                )}
              </div>
              <div className="mt-4 flex items-center gap-3">
                <span className="text-3xl font-bold text-green-700">
                  ₹<InlineEditableTextWithNav 
                    p={p} 
                    field={selectedProduct.priceKey} 
                    value={selectedProduct.price.toString()}
                    onNavigate={handleNavigateAfterEdit}
                    navPage={activePage}
                    isInModal={true}
                  />
                </span>
                {selectedProduct.originalPrice > selectedProduct.price && (
                  <span className="text-sm text-gray-400 line-through">
                    ₹<InlineEditableTextWithNav 
                      p={p} 
                      field={selectedProduct.originalPriceKey} 
                      value={selectedProduct.originalPrice.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage={activePage}
                      isInModal={true}
                    />
                  </span>
                )}
              </div>
              <div className="mt-4 text-sm text-green-600 text-center bg-green-50 p-3 rounded-full border border-green-200">
                🌾 Quality agricultural product for your farm
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Guide Modal - FIXED with InlineEditableImage */}
      {selectedGuide && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-2xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl animate-slide-up max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 bg-white border-b border-gray-100 p-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-green-900">Farming Guide</h3>
              <button 
                onClick={closeGuideModal} 
                className="w-10 h-10 rounded-full bg-gray-100 text-gray-600 hover:bg-green-600 hover:text-white transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <InlineEditableImage p={p} field={selectedGuide.imageField} className="block w-full overflow-hidden rounded-xl">
                  <img src={selectedGuide.image} alt={selectedGuide.title} className="w-full h-64 object-cover" />
                </InlineEditableImage>
              </div>
              <div className="mb-3">
                <span className="inline-block px-3 py-1 bg-green-100 text-green-800 text-xs rounded-full font-semibold">
                  {selectedGuide.difficulty}
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-green-900 mb-4">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedGuide.titleKey} 
                  value={selectedGuide.title}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="guides"
                  isInModal={true}
                />
              </h2>
              <p className="text-gray-600 mb-6">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedGuide.descKey} 
                  value={selectedGuide.desc}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="guides"
                  isInModal={true}
                />
              </p>
              <div className="space-y-4">
                <p className="font-semibold text-green-800">Steps:</p>
                {selectedGuide.steps?.map((step, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-green-50 rounded-xl">
                    <span className="w-8 h-8 rounded-full bg-green-700 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {idx + 1}
                    </span>
                    <p className="text-gray-700 text-sm">{step}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex justify-end">
                <button 
                  onClick={closeGuideModal} 
                  className="px-6 py-2 rounded-full bg-green-700 text-white hover:bg-green-800 transition"
                >
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="close_button" 
                    value="Close"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="guides"
                    isInModal={true}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Blog Modal - FIXED with InlineEditableImage */}
      {selectedPost && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-2xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 bg-white border-b border-gray-100 p-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-green-900">Agriculture Insights</h3>
              <button 
                onClick={closeBlogModal} 
                className="w-10 h-10 rounded-full bg-gray-100 text-gray-600 hover:bg-green-600 hover:text-white transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <InlineEditableImage p={p} field={selectedPost.imageField} className="block w-full overflow-hidden rounded-xl">
                  <img src={selectedPost.image} alt={selectedPost.title} className="w-full h-64 object-cover" />
                </InlineEditableImage>
              </div>
              <div className="mb-3">
                <span className="inline-block px-3 py-1 bg-green-100 text-green-800 text-xs rounded-full font-semibold">
                  {selectedPost.category}
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-green-900 mb-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedPost.titleKey} 
                  value={selectedPost.title}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="journal"
                  isInModal={true}
                />
              </h2>
              <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 border-b border-gray-100 pb-4 mb-4">
                <span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.dateKey} 
                    value={selectedPost.date}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </span>
                <span>•</span>
                <span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="by_prefix" 
                    value="By"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />{' '}
                  <span className="font-semibold text-green-900">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={selectedPost.authorKey} 
                      value={selectedPost.author}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                      isInModal={true}
                    />
                  </span>
                </span>
              </div>
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.fullContentKey} 
                    value={selectedPost.fullContent}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </p>
              </div>
              <div className="mt-8 pt-4 border-t border-gray-100 flex justify-end">
                <button 
                  onClick={closeBlogModal} 
                  className="px-6 py-2 rounded-full bg-green-700 text-white hover:bg-green-800 transition"
                >
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="close_button" 
                    value="Close"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        @keyframes marquee { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }
        .animate-marquee { animation: marquee 20s linear infinite; }
        
        @keyframes float-particle { 0%,100% { transform: translateY(0px) translateX(0px); opacity: 0.05; } 50% { transform: translateY(-40px) translateX(20px); opacity: 0.2; } }
        .animate-float-particle { animation: float-particle 6s ease-in-out infinite; }
        
        @keyframes word-pop { 0% { opacity: 0; transform: scale(0.5) rotate(-8deg); } 60% { transform: scale(1.08) rotate(2deg); } 100% { opacity: 1; transform: scale(1) rotate(0); } }
        .animate-word-pop { animation: word-pop 0.6s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes slide-right { from { opacity: 0; transform: translateX(-40px); } to { opacity: 1; transform: translateX(0); } }
        .animate-slide-right { animation: slide-right 0.7s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        
        @keyframes bounce-in { 0% { opacity: 0; transform: scale(0.3); } 50% { transform: scale(1.05); } 70% { transform: scale(0.95); } 100% { opacity: 1; transform: scale(1); } }
        .animate-bounce-in { animation: bounce-in 0.7s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes card-fly { 0% { opacity: 0; transform: translateY(40px) scale(0.9); } 60% { transform: translateY(-5px) scale(1.02); } 100% { opacity: 1; transform: translateY(0) scale(1); } }
        .animate-card-fly { animation: card-fly 0.6s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes scroll-dot { 0% { transform: translateY(0); opacity: 1; } 100% { transform: translateY(12px); opacity: 0; } }
        .animate-scroll-dot { animation: scroll-dot 1.5s ease-in-out infinite; }
        
        @keyframes slide-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-slide-up { animation: slide-up 0.5s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
        
        @keyframes bounce { 0%,100% { transform: translateX(-50%) translateY(0); } 50% { transform: translateX(-50%) translateY(-8px); } }
        .animate-bounce-slow { animation: bounce 2s ease-in-out infinite; }
        
        @keyframes slide-up-modal { from { opacity: 0; transform: translateY(30px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .animate-slide-up { animation: slide-up-modal 0.4s ease-out forwards; }
        
        .line-clamp-1 { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>
    </div>
  );
}